import { useCallback, useEffect, useMemo, useState } from "react";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { esc, n0, n2, printHtml } from "../../lib/format";
import { Modal, Notice, Stat } from "../../lib/ui";
import "./services.css";

/*
 * Service catalog & service sales – port of ServiceList.php (API: /api/services..., backend/services.py)
 * Admins add, edit and delete services and may adjust a cart line's amount. Anyone can sell:
 * the sale is saved to service_payments and a pending SERVICE payment is created for verification.
 */

const METHODS = ["Cash", "M-Pesa", "Bank Transfer", "POS Card"];
const BLANK_SVC = { service_name: "", category: "", unit_price: "", status: "Active", description: "" };
const BLANK_CLIENT = { client_name: "", contact_email: "", contact_phone: "", address: "" };

function receiptHtml(r) {
  const rows = r.lines.map((l) => `<tr><td>${esc(l.name)}</td><td class="c">${l.qty}</td><td class="r">${n2(l.unit)}</td><td class="r">${n2(l.sub)}</td></tr>`).join("");
  return `<!doctype html><html><head><meta charset="utf-8"><title>${r.receipt_no}</title><style>
    body{font-family:'Segoe UI',Arial,sans-serif;font-size:12px;margin:0;padding:14px;width:330px;color:#111}h3{margin:0;text-align:center}
    .s{text-align:center;font-size:11px;color:#555;margin-bottom:8px}.m div{display:flex;justify-content:space-between;padding:1px 0}
    table{width:100%;border-collapse:collapse;margin-top:8px}th{text-align:left;border-bottom:1px solid #333}td{padding:4px 0;border-bottom:1px dashed #ccc}
    .r{text-align:right}.c{text-align:center}.t{font-weight:700;font-size:14px;display:flex;justify-content:space-between;margin-top:8px}
    .e{text-align:center;margin-top:12px;font-size:11px;color:#666}</style></head><body>
    <h3>AE2C ENTERPRISE</h3><div class="s">Service receipt</div>
    <div class="m"><div><span>Receipt no.</span><b>${esc(r.receipt_no)}</b></div><div><span>Date</span><span>${esc(r.time)}</span></div>
    <div><span>Client</span><span>${esc(r.client)}</span></div><div><span>Method</span><span>${esc(r.method)}</span></div>
    ${r.ref ? `<div><span>Reference</span><span>${esc(r.ref)}</span></div>` : ""}<div><span>Cashier</span><span>${esc(r.cashier)}</span></div></div>
    <table><thead><tr><th>Service</th><th class="c">Qty</th><th class="r">Unit</th><th class="r">Amount</th></tr></thead><tbody>${rows}</tbody></table>
    <div class="t"><span>Total</span><span>TZS ${n2(r.total)}</span></div><div class="e">Thank you!</div></body></html>`;
}

export default function ServiceCatalog({ user }) {
  const api = useApi();
  const admin = user?.role === "admin";
  const [services, setServices] = useState(null);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [notice, setNotice] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [status, setStatus] = useState("");
  const [cart, setCart] = useState({}); // id -> {name, cat, qty, price, override}
  const [clientId, setClientId] = useState("");
  const [method, setMethod] = useState("Cash");
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [svcModal, setSvcModal] = useState(null); // {mode, form, id?}
  const [delModal, setDelModal] = useState(null);
  const [clientModal, setClientModal] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [s, st, c] = await Promise.all([api.get("/api/services"), api.get("/api/services/stats"), api.get("/api/clients")]);
      setServices(s); setStats(st); setClients(c);
    } catch (e) { setNotice({ tone: "err", text: e.message }); }
  }, [api]);
  useEffect(() => { loadAll(); }, [loadAll]);

  const categories = useMemo(() => [...new Set((services || []).map((s) => s.category).filter(Boolean))].sort(), [services]);
  const shown = useMemo(() => (services || []).filter((s) => {
    const t = q.trim().toLowerCase();
    return (!t || [s.service_name, s.category, s.description].some((v) => (v || "").toLowerCase().includes(t)))
      && (!cat || s.category === cat) && (!status || s.status === status);
  }), [services, q, cat, status]);

  const lines = Object.entries(cart);
  const total = lines.reduce((s, [, l]) => s + l.price * l.qty, 0);
  const add = (s) => setCart((c) => ({ ...c, [s.service_id]: c[s.service_id]
    ? { ...c[s.service_id], qty: c[s.service_id].qty + 1 }
    : { name: s.service_name, cat: s.category, qty: 1, price: s.unit_price, override: false } }));
  const chQty = (id, d) => setCart((c) => {
    const l = c[id]; const qty = l.qty + d;
    if (qty < 1) { const { [id]: _, ...rest } = c; return rest; }
    return { ...c, [id]: { ...l, qty } };
  });
  const setAmount = (id, v) => setCart((c) => {
    const amt = Math.max(0, parseFloat(v) || 0);
    return { ...c, [id]: { ...c[id], price: c[id].qty > 0 ? amt / c[id].qty : amt, override: true } };
  });

  const sell = async () => {
    setBusy(true); setNotice(null);
    try {
      const r = await api.post("/api/services/sell", {
        items: lines.map(([id, l]) => ({ service_id: Number(id), qty: l.qty, override_price: l.override ? l.price : null })),
        payment_method: method, reference_number: ref, client_id: clientId || null,
      });
      setReceipt(r); setCart({}); setRef("");
      setNotice({ tone: "ok", text: `${r.message} Receipt ${r.receipt_no}, TZS ${n0(r.total)}. It now waits in Pending Transactions for verification.` });
    } catch (e) { setNotice({ tone: "err", text: e.message }); }
    finally { setBusy(false); }
  };

  const saveSvc = async (e) => {
    e.preventDefault();
    try {
      const r = svcModal.mode === "add" ? await api.post("/api/services", svcModal.form) : await api.request(`/api/services/${svcModal.id}`, "PUT", svcModal.form);
      setNotice({ tone: "ok", text: r.message }); setSvcModal(null); loadAll();
    } catch (err) { setSvcModal((m) => ({ ...m, error: err.message })); }
  };
  const deleteSvc = async () => {
    const s = delModal; setDelModal(null);
    try { const r = await api.request(`/api/services/${s.service_id}`, "DELETE"); setNotice({ tone: "ok", text: r.message }); loadAll(); }
    catch (e) { setNotice({ tone: "err", text: e.message }); }
  };
  const saveClient = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/api/clients", clientModal.form);
      setClients((c) => [...c, r.client].sort((a, b) => a.name.localeCompare(b.name)));
      setClientId(String(r.client.id)); setClientModal(null);
      setNotice({ tone: "ok", text: r.message });
    } catch (err) { setClientModal((m) => ({ ...m, error: err.message })); }
  };
  const setSvcField = (k) => (e) => setSvcModal((m) => ({ ...m, form: { ...m.form, [k]: e.target.value } }));
  const setClientField = (k) => (e) => setClientModal((m) => ({ ...m, form: { ...m.form, [k]: e.target.value } }));

  const actions = (
    <>
      <span className={`pill-tag ${admin ? "brand" : ""}`}>{admin ? "Admin · full control" : "Staff · selling only"}</span>
      <button type="button" className="btn" onClick={loadAll} aria-label="Refresh"><i className="fas fa-rotate-right" /></button>
      {admin && <button type="button" className="btn primary" onClick={() => setSvcModal({ mode: "add", form: BLANK_SVC })}><i className="fas fa-plus" /> New service</button>}
    </>
  );

  return (
    <main className="pg" style={{ maxWidth: "90rem" }}>
      <PageHeader slug="ServiceList" actions={actions} />
      <Notice notice={notice} onClose={() => setNotice(null)} />
      {stats && (
        <div className="ui-stats">
          <Stat icon="fa-list-check" label="Services" value={stats.total} />
          <Stat icon="fa-circle-check" tone="up" label="Active" value={stats.active} />
          <Stat icon="fa-cart-shopping" tone="warn" label="In cart" value={lines.length} />
        </div>
      )}
      <div className="sv-grid">
        <section className="panel">
          <div className="toolbar">
            <div className="field grow"><div className="search"><i className="fas fa-magnifying-glass" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search services" aria-label="Search services" /></div></div>
            <div className="field"><select aria-label="Category" value={cat} onChange={(e) => setCat(e.target.value)}>
              <option value="">All categories</option>{categories.map((c) => <option key={c}>{c}</option>)}</select></div>
            <div className="field"><select aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All status</option><option>Active</option><option>Inactive</option></select></div>
          </div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead><tr><th>#</th><th>Service</th><th>Category</th><th className="num">Unit price</th><th>Status</th><th /></tr></thead>
              <tbody>
                {shown.map((s) => (
                  <tr key={s.service_id}>
                    <td className="mono">{s.service_id}</td>
                    <td><div style={{ fontWeight: 600, color: "var(--ink)" }}>{s.service_name}</div>{s.description && <div style={{ fontSize: ".75rem", color: "var(--muted)" }}>{s.description}</div>}</td>
                    <td>{s.category}</td><td className="num mono">{n2(s.unit_price)}</td>
                    <td><span className={`pill-tag ${s.status === "Active" ? "ok" : ""}`}>{s.status}</span></td>
                    <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                      {s.status === "Active" && <button type="button" className="btn sm primary" onClick={() => add(s)}><i className="fas fa-cart-plus" /> Add</button>}{" "}
                      {admin && <>
                        <button type="button" className="btn sm" aria-label={`Edit ${s.service_name}`} onClick={() => setSvcModal({ mode: "edit", id: s.service_id, form: { ...s, unit_price: String(s.unit_price), description: s.description || "" } })}><i className="fas fa-pen" /></button>{" "}
                        <button type="button" className="btn sm" aria-label={`Delete ${s.service_name}`} onClick={() => setDelModal(s)}><i className="fas fa-trash-can" /></button>
                      </>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {services && shown.length === 0 && <div className="empty"><i className="fas fa-list" />No services match.</div>}
            {!services && <div className="empty"><i className="fas fa-spinner fa-spin" />Loading services…</div>}
          </div>
        </section>

        <section className="panel sv-cart" aria-label="Service sale">
          <div className="sv-cart-hd"><h2>New sale</h2>{lines.length > 0 && <button type="button" className="linkbtn" onClick={() => setCart({})}>Clear</button>}</div>
          <div className="sv-cart-body">
            {lines.length === 0 ? <div className="cart-empty" style={{ padding: "1.5rem 0" }}><i className="fas fa-cart-shopping" />Add services from the list.</div>
              : lines.map(([id, l]) => (
                <div key={id} className="sv-line">
                  <div className="sv-line-top"><span className="nm">{l.name}</span>
                    {admin ? <input className="cell" type="number" min="0" step="0.01" aria-label={`Amount for ${l.name}`} value={Math.round(l.price * l.qty * 100) / 100}
                      onChange={(e) => setAmount(id, e.target.value)} style={{ maxWidth: "8rem", textAlign: "right" }} />
                      : <span className="mono">{n2(l.price * l.qty)}</span>}</div>
                  <div className="sv-line-ctl">
                    <span className="qty"><button type="button" onClick={() => chQty(id, -1)} aria-label="One less">−</button><span className="mono">{l.qty}</span>
                      <button type="button" onClick={() => chQty(id, 1)} aria-label="One more">+</button></span>
                    {l.override && <span className="pill-tag warn">Adjusted</span>}
                    <span style={{ fontSize: ".75rem", color: "var(--muted)" }}>{l.cat}</span>
                  </div>
                </div>
              ))}
            <div className="field" style={{ marginTop: ".9rem" }}>
              <label htmlFor="sv-client">Client <span style={{ fontWeight: 400, color: "var(--muted)" }}>(optional)</span></label>
              <div style={{ display: "flex", gap: ".4rem" }}>
                <select id="sv-client" value={clientId} onChange={(e) => setClientId(e.target.value)}>
                  <option value="">Walk-in / anonymous</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <button type="button" className="btn sm" onClick={() => setClientModal({ form: BLANK_CLIENT })} aria-label="Add client"><i className="fas fa-user-plus" /></button>
              </div>
            </div>
            <div className="field" style={{ marginTop: ".8rem" }}><label id="sv-m">Method</label>
              <div className="seg sv-methods" role="group" aria-labelledby="sv-m">
                {METHODS.map((m) => <button key={m} type="button" aria-pressed={method === m} onClick={() => setMethod(m)}>{m}</button>)}</div></div>
            <div className="field" style={{ marginTop: ".8rem" }}><label htmlFor="sv-ref">Reference / txn no. <span style={{ fontWeight: 400, color: "var(--muted)" }}>(optional)</span></label>
              <input id="sv-ref" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. TXN-12345" /></div>
          </div>
          <div className="pos-pay">
            <div className="sums"><div className="grand"><span>Total</span><span className="mono">TZS {n2(total)}</span></div></div>
            <div className="pos-actions"><button type="button" className="btn-pay" disabled={!lines.length || busy} onClick={sell}>{busy ? "Recording…" : "Receive payment"}</button></div>
            {receipt && (
              <div className="sv-receipt">
                <span>Last receipt <b className="mono">{receipt.receipt_no}</b> · TZS {n0(receipt.total)}</span>
                <button type="button" className="btn sm" onClick={() => printHtml(receiptHtml(receipt))}><i className="fas fa-print" /> Print</button>
              </div>
            )}
          </div>
        </section>
      </div>

      {svcModal && (
        <Modal title={svcModal.mode === "add" ? "New service" : "Edit service"} onClose={() => setSvcModal(null)}
          actions={<><button type="button" className="btn" onClick={() => setSvcModal(null)}>Cancel</button><button type="submit" form="sv-form" className="btn primary">Save</button></>}>
          <form id="sv-form" onSubmit={saveSvc} className="sv-modal-form">
            {svcModal.error && <div className="ui-notice err">{svcModal.error}</div>}
            <div className="field"><label htmlFor="sv-n">Service name<span className="req">*</span></label><input id="sv-n" required value={svcModal.form.service_name} onChange={setSvcField("service_name")} placeholder="e.g. Document printing" /></div>
            <div className="field"><label htmlFor="sv-c">Category<span className="req">*</span></label><input id="sv-c" required list="sv-cats" value={svcModal.form.category} onChange={setSvcField("category")} placeholder="e.g. Printing" />
              <datalist id="sv-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist></div>
            <div className="field"><label htmlFor="sv-p">Unit price (TZS)<span className="req">*</span></label><input id="sv-p" type="number" min="0" step="0.01" required value={svcModal.form.unit_price} onChange={setSvcField("unit_price")} /></div>
            <div className="field"><label htmlFor="sv-s">Status</label><select id="sv-s" value={svcModal.form.status} onChange={setSvcField("status")}><option>Active</option><option>Inactive</option></select></div>
            <div className="field"><label htmlFor="sv-d">Description</label><textarea id="sv-d" rows={3} value={svcModal.form.description} onChange={setSvcField("description")} /></div>
          </form>
        </Modal>
      )}
      {delModal && (
        <Modal title="Delete service?" onClose={() => setDelModal(null)}
          actions={<><button type="button" className="btn" onClick={() => setDelModal(null)}>Cancel</button><button type="button" className="btn danger" onClick={deleteSvc}>Delete</button></>}>
          <b>{delModal.service_name}</b> will be removed. Services that have been sold can't be deleted; set them Inactive instead.
        </Modal>
      )}
      {clientModal && (
        <Modal title="Add client" onClose={() => setClientModal(null)}
          actions={<><button type="button" className="btn" onClick={() => setClientModal(null)}>Cancel</button><button type="submit" form="cl-form" className="btn primary">Add client</button></>}>
          <form id="cl-form" onSubmit={saveClient} className="sv-modal-form">
            {clientModal.error && <div className="ui-notice err">{clientModal.error}</div>}
            <div className="field"><label htmlFor="cl-n">Full name<span className="req">*</span></label><input id="cl-n" required value={clientModal.form.client_name} onChange={setClientField("client_name")} /></div>
            <div className="field"><label htmlFor="cl-e">Email</label><input id="cl-e" type="email" value={clientModal.form.contact_email} onChange={setClientField("contact_email")} /></div>
            <div className="field"><label htmlFor="cl-p">Phone</label><input id="cl-p" value={clientModal.form.contact_phone} onChange={setClientField("contact_phone")} placeholder="+255 7xx xxx xxx" /></div>
            <div className="field"><label htmlFor="cl-a">Address</label><input id="cl-a" value={clientModal.form.address} onChange={setClientField("address")} /></div>
          </form>
        </Modal>
      )}
    </main>
  );
}
